import Footer from "@/components/elements/Footer";
import Navbar from "@/components/elements/Navbar";
import { useGetCurrentUrl } from "@/hooks";
import useAuth from "@/hooks/auth";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import PaymentSuccess from "../PaymentSuccess";
import Login from "../modal/Login";
import Register from "../modal/Register";
import Subscribe from "../modal/Subscribe";
import { useAtom } from "jotai";
import {
  subscribePopup,
  purchasePopup,
  purchaseStartupPopup,
  registerPopup,
  audioAtom,
  purchaseVCPopup,
  purchaseAngelInvestorPopup,
  curatedShortlistModal,
} from "@/Atoms";
import Purchase from "../modal/Purchase";
import PurchaseStartup from "../modal/PurchaseStartup";
import AudioPlayer from "@/components/audio/AudioPlayer";
import PurchaseVC from "../modal/PurchaseVC";
import PurchaseAngelInvestor from "../modal/PurchaseAngelInvestor";
import { useIsMobile } from "@/hooks/useIsMobile";

const Layout = ({
  children,
  title,
  meta_title,
  description,
  seoData,
  hideTopMobileNavigation = false,
  hideBottomMobileNavigation = false,

  ...props
}) => {
  const router = useRouter();
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [openSubscribePopup, setOpenSubscribePopup] = useAtom(subscribePopup);
  const currentURL = useGetCurrentUrl();
  const { user, isLoading: isAuthLoading, logout } = useAuth();
  const [openPurchasePopup, setOpenPurchasePopup] = useAtom(purchasePopup);
  const [openPurchaseStartupPopup, setOpenPurchaseStartupPopup] =
    useAtom(purchaseStartupPopup);
  const [openRegisterPopup, setOpenRegisterPopup] = useAtom(registerPopup);
  const [currentAudio, setCurrentAudio] = useAtom(audioAtom);
  const [openPurchaseVCPopup, setOpenPurchaseVCPopup] =
    useAtom(purchaseVCPopup);
  const [openPurchaseAngelInvestorPopup, setOpenPurchaseAngelInvestorPopup] =
    useAtom(purchaseAngelInvestorPopup);
  const [openCuratedModal, setOpenCuratedModal] = useAtom(curatedShortlistModal);

  useEffect(() => {
    const { query } = router;
    const paymentStatus = query.payment;
    const action = query.action;

    if (
      paymentStatus === "success-content" ||
      paymentStatus === "success-startup"
    ) {
      setShowPaymentSuccess(true);
    }

    if (paymentStatus === "success-content") {
      setSubscriptionType("content");
    }
    if (paymentStatus === "success-startup") {
      setSubscriptionType("startup");
    }

    if (
      localStorage.getItem("payment") &&
      localStorage.getItem("payment") === "Startup Registration"
    ) {
      if (localStorage.getItem("nextStep") !== "checkProfileCompleted") {
        // setShowPaymentSuccess(true);
        // router.push('/startupregistration')
      }
      // setSubscriptionType("startup");
      localStorage.removeItem("payment");
    }

    if (
      localStorage.getItem("payment") &&
      localStorage.getItem("payment") === "Content Access"
    ) {
      if (localStorage.getItem("nextStep") !== "checkProfileCompleted") {
        setShowPaymentSuccess(true);
      }
      setSubscriptionType("content");
      localStorage.removeItem("payment");
    }

    if (action == "openRegisterPopup") {
      localStorage.setItem("nextStep", "openRegisterPopup");
      location.href = "/";
    }

    if (localStorage.getItem("lcdt-podcast")) {
      setCurrentAudio(JSON.parse(localStorage.getItem("lcdt-podcast")));
    }
  }, []);

  const getNextStep = (_nextStep) => {
    switch (_nextStep) {
      case "purchasePopup":
        setOpenPurchasePopup(
          localStorage.getItem("purchasePopupType") || "content"
        );
        localStorage.removeItem("nextStep", "");
        localStorage.removeItem("purchasePopupType");
        break;
      case "purchaseStartupPopup":
        setOpenPurchaseStartupPopup(true);
        localStorage.removeItem("nextStep", "");
        break;
      case "openRegisterPopup":
        setOpenRegisterPopup(true);
        localStorage.removeItem("nextStep", "");
        break;
      case "accessMyAccessLink":
        router.push("/account/my-accesses");
        localStorage.removeItem("nextStep", "");
        break;
      case "curatedShortlistModal":
        setOpenCuratedModal(true);
        localStorage.removeItem("nextStep", "");
        break;
      case "goToUrl":
        const goToUrl = localStorage.getItem("nextStepUrl");
        if (goToUrl && user) {
          router.push(goToUrl);
        }
        localStorage.removeItem("nextStep", "");
        localStorage.removeItem("nextStepUrl", "");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isAuthLoading || user) return;
    const timeoutId = setTimeout(() => {
      if (localStorage.getItem("lc_already_open_newsletter") !== "false") {
        setOpenSubscribePopup(true);
        localStorage.setItem("lc_already_open_newsletter", "true");
      }
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [isAuthLoading, user]);

  // Process nextStep only when user is authenticated
  useEffect(() => {
    if (!user) {
      // Close modals that require auth when user logs out
      setOpenCuratedModal(false);
      return;
    }
    const nextStep = localStorage.getItem("nextStep");
    if (nextStep && router.pathname !== "/auth/google") {
      getNextStep(nextStep);
    }
  }, [user]);

  // Use seoData to override meta_title and description if available
  const effectiveMetaTitle = seoData?.meta_title || meta_title || title;
  const effectiveDescription = seoData?.meta_description || description;

  const isMobile = useIsMobile();

  return (
    <>
      <Head>
        <title>{effectiveMetaTitle}</title>
        <meta name='title' content={effectiveMetaTitle} />
        <meta name='description' content={effectiveDescription} />
        <meta property='og:title' content={effectiveMetaTitle} key='title' />
        <meta property='og:description' content={effectiveDescription} />
        <meta name='twitter:title' content={effectiveMetaTitle} />
        <meta name='twitter:description' content={effectiveDescription} />
        <meta
          property='og:image'
          content={
            props.meta_image ||
            "https://cdn.lucidityinsights.com/thumbnaildefault.jpg"
          }
        />
        <meta
          name='twitter:image'
          content={
            props.meta_image ||
            "https://cdn.lucidityinsights.com/thumbnaildefault.jpg"
          }
        />
        <meta name='twitter:card' content='summary_large_image' />
        <meta property='og:type' content='article' />
        <meta property='og:site_name' content='Lucidity Insights' />
        <meta property='og:url' content={currentURL} />
        <meta property='og:title' content={effectiveMetaTitle} />
        <meta property='og:description' content={effectiveDescription} />
        <meta
          property='og:image'
          content={
            props.meta_image ||
            "https://cdn.lucidityinsights.com/thumbnaildefault.jpg"
          }
        />
        <meta
          property='og:image:secure_url'
          content={
            props.meta_image ||
            "https://cdn.lucidityinsights.com/thumbnaildefault.jpg"
          }
        />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='630' />
        <meta property='og:image:type' content='image/jpeg' />
        <meta property='og:image:alt' content={effectiveMetaTitle} />
        {/* <meta property='fb:app_id' content='YOUR_FACEBOOK_APP_ID' /> */}
        <link rel='icon' href='/favicon.png' />
      </Head>
      <PaymentSuccess
        open={showPaymentSuccess}
        setOpen={setShowPaymentSuccess}
        type={subscriptionType}
      />

      <Navbar
        hideTopMobileNavigation={hideTopMobileNavigation}
        hideBottomMobileNavigation={hideBottomMobileNavigation}
        user={user}
        logout={logout}
      />

      {children}
      <Register />
      <Login />
      <Subscribe />
      {currentAudio && <AudioPlayer />}
      {openPurchasePopup && user && <Purchase />}
      {openPurchaseStartupPopup && user && <PurchaseStartup />}
      {openPurchaseVCPopup && user && <PurchaseVC />}
      {openPurchaseAngelInvestorPopup && user && <PurchaseAngelInvestor />}
      {!currentURL.includes("/account") &&
        !currentURL.includes("/messages") && <Footer />}
    </>
  );
};

export default Layout;
